import { motion } from "framer-motion";
import { ArrowRight, Sparkles, TrendingUp, Bot } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useState } from "react";
import AppointmentDialog from "@/components/AppointmentDialog";
import { Link } from "react-router-dom";

const HERO_IMG =
  "https://static.prod-images.emergentagent.com/jobs/b3aef13b-f596-4d0d-a8af-4fa0fa7ae7f3/images/8e6966ff3a58e273a59ce4507e6a8c706b1e8f536fdc1f1538f05c76ecf96e2a.png";

export default function Hero() {
  const [open, setOpen] = useState(false);

  return (
    <section className="relative overflow-hidden bg-white" data-testid="hero-section">
      <div className="absolute inset-0 bg-grid opacity-60 pointer-events-none" />
      <div className="absolute -top-40 -right-40 h-[500px] w-[500px] rounded-full bg-[#00B8D9]/10 blur-3xl pointer-events-none" />
      <div className="absolute -bottom-40 -left-40 h-[500px] w-[500px] rounded-full bg-[#0B1F3A]/5 blur-3xl pointer-events-none" />

      <div className="relative mx-auto max-w-7xl px-6 md:px-10 pt-20 pb-24 lg:pt-28 lg:pb-32 grid lg:grid-cols-2 gap-12 items-center">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#00B8D9]/10 border border-[#00B8D9]/20 text-[#00B8D9] text-xs font-bold tracking-[0.18em] uppercase mb-6">
            <Sparkles className="h-3.5 w-3.5" />
            Agence numérique · Montréal
          </div>

          <h1 className="font-heading font-black text-4xl sm:text-5xl lg:text-6xl text-[#0B1F3A] leading-[1.05] tracking-tighter text-balance">
            Développez votre entreprise grâce au <span className="text-[#00B8D9]">numérique</span> et à l'<span className="text-[#00B8D9]">intelligence artificielle</span>
          </h1>

          <p className="mt-6 text-lg text-[#4B5563] leading-relaxed max-w-xl">
            Nous aidons les PME à attirer plus de clients, automatiser leurs opérations et augmenter leurs revenus grâce à des solutions numériques adaptées à leur réalité.
          </p>

          <div className="mt-8 flex flex-col sm:flex-row gap-3">
            <Button
              onClick={() => setOpen(true)}
              data-testid="hero-cta-diagnostic"
              className="bg-[#00B8D9] hover:bg-[#0096B3] text-white font-semibold rounded-md px-7 py-6 text-base shadow-lg shadow-[#00B8D9]/25 group"
            >
              Obtenir un diagnostic gratuit
              <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
            </Button>
            <Button
              asChild
              variant="outline"
              data-testid="hero-cta-soumission"
              className="border-2 border-[#0B1F3A] text-[#0B1F3A] hover:bg-[#0B1F3A] hover:text-white font-semibold rounded-md px-7 py-6 text-base"
            >
              <Link to="/cahier-des-charges">Démarrer un projet</Link>
            </Button>
          </div>

          <div className="mt-10 flex items-center gap-6 text-sm text-[#4B5563]">
            <div className="flex items-center gap-2"><TrendingUp className="h-4 w-4 text-[#00B8D9]" /> Croissance mesurable</div>
            <div className="flex items-center gap-2"><Bot className="h-4 w-4 text-[#00B8D9]" /> IA intégrée</div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.7, delay: 0.2 }}
          className="relative"
        >
          <div className="relative rounded-2xl overflow-hidden border border-[#0B1F3A]/10 shadow-2xl">
            <img src={HERO_IMG} alt="IA et croissance numérique" className="w-full aspect-square object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0B1F3A]/40 via-transparent to-transparent" />
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.7 }}
            className="absolute -left-4 lg:-left-12 bottom-12 bg-white rounded-xl shadow-xl border border-gray-200 p-4 max-w-[220px]"
          >
            <div className="text-[10px] tracking-[0.18em] uppercase font-bold text-[#00B8D9] mb-1">Cas client</div>
            <div className="text-2xl font-heading font-extrabold text-[#0B1F3A]">+63%</div>
            <div className="text-xs text-[#4B5563]">de revenus en 12 mois pour une PME industrielle</div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.9 }}
            className="absolute -right-2 lg:-right-8 top-12 bg-[#0B1F3A] text-white rounded-xl shadow-xl p-4 max-w-[220px]"
          >
            <div className="text-[10px] tracking-[0.18em] uppercase font-bold text-[#00B8D9] mb-1">Automatisation IA</div>
            <div className="text-2xl font-heading font-extrabold">18h/sem</div>
            <div className="text-xs text-gray-300">économisées en moyenne par client</div>
          </motion.div>
        </motion.div>
      </div>
      <AppointmentDialog open={open} onOpenChange={setOpen} defaultType="diagnostic" />
    </section>
  );
}
